import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useSendFamilyInviteMutation } from '@backrs_ui/grow-ui/src/redux/family/family.api';

import {
  Alert,
  Box,
  SxProps,
  Theme,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Autocomplete,
  Snackbar,
} from '@mui/material';

import { OnboardingStepContainer } from '../components/OnboardingStepContainer';
import { OnboardingStepHeader } from '../components/OnboardingStepHeader';

const STRINGS = appStrings.modules.onboarding.inviteFamily;
const SNACKBAR_DURATION = 2000; // 2 seconds before navigating

type FamilyInvitee = Readonly<{
  name: string;
  role: string;
  email: string;
}>;

type InviteeErrors = Partial<Record<keyof FamilyInvitee, string>>;

type InviteFamilyStepProps = Readonly<{
  onComplete: () => void;
  maxSeats?: number;
}>;

const ROLE_OPTIONS = [
  { label: 'Parent/Guardian', value: 'parent/guardian' },
  { label: 'Student', value: 'student' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FAMILY_INVITE_FIELDS: readonly (keyof FamilyInvitee)[] = ['name', 'role', 'email'] as const;

const emptyInvitee: FamilyInvitee = {
  name: '',
  role: '',
  email: '',
};

const validateInvitee = (invitee: FamilyInvitee): InviteeErrors => {
  const errors: InviteeErrors = {};
  const { validation } = STRINGS;

  if (!invitee.name.trim()) {
    errors.name = validation.nameRequired;
  }

  if (!invitee.role) {
    errors.role = validation.roleRequired;
  }

  if (!invitee.email.trim()) {
    errors.email = validation.emailRequired;
  } else if (!EMAIL_REGEX.test(invitee.email)) {
    errors.email = validation.emailInvalid;
  }

  return errors;
};

const isInviteeValid = (invitee: FamilyInvitee): boolean => {
  return Object.keys(validateInvitee(invitee)).length === 0;
};

const getFieldErrorProps = (
  field: keyof FamilyInvitee,
  touched: Set<keyof FamilyInvitee>,
  errors: InviteeErrors,
) => ({
  error: touched.has(field) && !!errors[field],
  helperText: touched.has(field) ? errors[field] : undefined,
});

export const InviteFamilyStep = ({ onComplete, maxSeats = 6 }: InviteFamilyStepProps) => {
  const [invitees, setInvitees] = useState<FamilyInvitee[]>([{ ...emptyInvitee }]);
  const [errors, setErrors] = useState<InviteeErrors[]>([{}]);
  const [touchedFields, setTouchedFields] = useState<Record<number, Set<keyof FamilyInvitee>>>({
    0: new Set(),
  });

  const [sendFamilyInvite] = useSendFamilyInviteMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const maxInvitees = maxSeats;

  const handleInviteeChange = (index: number, field: keyof FamilyInvitee, value: string) => {
    const updatedInvitees = [...invitees];

    updatedInvitees[index] = { ...updatedInvitees[index], [field]: value };
    setInvitees(updatedInvitees);

    // Clear error for this field when user starts typing
    if (errors[index]?.[field]) {
      const updatedErrors = [...errors];

      updatedErrors[index] = { ...updatedErrors[index], [field]: undefined };
      setErrors(updatedErrors);
    }
  };

  const handleFieldBlur = (index: number, field: keyof FamilyInvitee) => {
    // Mark field as touched
    setTouchedFields((prev) => {
      const newTouched = { ...prev };

      if (!newTouched[index]) {
        newTouched[index] = new Set();
      }

      newTouched[index] = new Set([...Array.from(newTouched[index]), field]);

      return newTouched;
    });

    // Validate the specific field
    const inviteeErrors = validateInvitee(invitees[index]);
    const updatedErrors = [...errors];

    updatedErrors[index] = {
      ...updatedErrors[index],
      [field]: inviteeErrors[field],
    };
    setErrors(updatedErrors);
  };

  const validateAllInvitees = (): boolean => {
    const allErrors: InviteeErrors[] = invitees.map((invitee) => validateInvitee(invitee));
    const allTouched: Record<number, Set<keyof FamilyInvitee>> = {};

    // Mark all fields as touched
    invitees.forEach((_, index) => {
      allTouched[index] = new Set(FAMILY_INVITE_FIELDS);
    });

    setErrors(allErrors);
    setTouchedFields(allTouched);

    return allErrors.every((err) => Object.keys(err).length === 0);
  };

  const handleAddInvitee = () => {
    // Validate all current invitees before adding a new one
    const lastInviteeIndex = invitees.length - 1;
    const lastInviteeErrors = validateInvitee(invitees[lastInviteeIndex]);

    // Mark all fields of last invitee as touched
    setTouchedFields((prev) => ({
      ...prev,
      [lastInviteeIndex]: new Set(FAMILY_INVITE_FIELDS),
    }));

    // Update errors for last invitee
    const updatedErrors = [...errors];

    updatedErrors[lastInviteeIndex] = lastInviteeErrors;
    setErrors(updatedErrors);

    // Only add new invitee if last invitee is valid
    if (Object.keys(lastInviteeErrors).length === 0 && invitees.length < maxInvitees) {
      setInvitees([...invitees, { ...emptyInvitee }]);
      setErrors([...updatedErrors, {}]);
      setTouchedFields((prev) => ({
        ...prev,
        [invitees.length]: new Set(),
      }));
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const handleSubmit = async () => {
    // Validate all invitees before submitting
    if (!validateAllInvitees()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // Filter out empty rows (user may have added but not filled)
    const validInvitees = invitees.filter((inv) => inv.email.trim());

    try {
      await sendFamilyInvite(
        validInvitees.map((invitee) => ({
          email: invitee.email,
          family_role: invitee.role,
          first_name: invitee.name,
        })),
      ).unwrap();

      // Show success snackbar then navigate after delay
      setShowSuccessMessage(true);
      setTimeout(() => {
        onComplete();
        setIsSubmitting(false);
      }, SNACKBAR_DURATION);
    } catch (error: unknown) {
      const apiError = error as { data?: { error?: string } };

      setSubmitError(apiError.data?.error || 'Failed to send invites');
      setIsSubmitting(false);
    }
  };

  // Check if the last invitee is valid (for enabling "Add another" button)
  const canAddInvitee =
    invitees.length < maxInvitees && isInviteeValid(invitees[invitees.length - 1]);

  return (
    <OnboardingStepContainer>
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" icon={false}>
          {STRINGS.invitesSent}
        </Alert>
      </Snackbar>
      <OnboardingStepHeader iconName="Mail05" title={STRINGS.title} subtitle={STRINGS.subtitle} />

      <Box sx={contentContainerSx}>
        <Box sx={formContainerSx}>
          <Box sx={inputFieldsSx}>
            {invitees.map((invitee, index) => {
              const inviteeErrors = errors[index] || {};
              const touched = touchedFields[index] || new Set();

              return (
                <Box key={index} sx={inviteeRowSx}>
                  <Box sx={inputRowSx}>
                    <TextField
                      id={`invite-family-name-${index}`}
                      placeholder="Name"
                      value={invitee.name}
                      onChange={(e) => handleInviteeChange(index, 'name', e.target.value)}
                      onBlur={() => handleFieldBlur(index, 'name')}
                      {...getFieldErrorProps('name', touched, inviteeErrors)}
                    />

                    <Autocomplete
                      id={`invite-family-role-${index}`}
                      options={ROLE_OPTIONS}
                      value={ROLE_OPTIONS.find((opt) => opt.value === invitee.role) || null}
                      onChange={(_, newValue) => {
                        handleInviteeChange(index, 'role', newValue?.value || '');
                      }}
                      onBlur={() => handleFieldBlur(index, 'role')}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      popupIcon={
                        <AppIcon
                          iconName="ChevronDown"
                          diameter="20px"
                          color="Colors/Foreground/fg-quaternary"
                        />
                      }
                      sx={roleSelectContainerSx}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="onboarding-invite-role-input"
                          placeholder="Role"
                          {...getFieldErrorProps('role', touched, inviteeErrors)}
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true,
                          }}
                        />
                      )}
                    />
                  </Box>

                  <TextField
                    id={`invite-family-email-${index}`}
                    fullWidth
                    placeholder="Name@example.com"
                    type="email"
                    value={invitee.email}
                    onChange={(e) => handleInviteeChange(index, 'email', e.target.value)}
                    onBlur={() => handleFieldBlur(index, 'email')}
                    {...getFieldErrorProps('email', touched, inviteeErrors)}
                    sx={{
                      outline: 'none',
                      '.MuiAutocomplete-inputRoot': 'none',
                      flex: '1 1 0',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AppIcon
                            iconName="Mail01"
                            diameter="20px"
                            color="Colors/Text/text-placeholder"
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              );
            })}

            {invitees.length < maxInvitees && (
              <Box
                id="invite-family-add-another-button"
                sx={addButtonContainerSx(canAddInvitee)}
                onClick={handleAddInvitee}
                onKeyDown={(e) => e.key === 'Enter' && handleAddInvitee()}
                role="button"
                tabIndex={0}
              >
                <AppIcon
                  iconName="Plus"
                  diameter="20px"
                  color={canAddInvitee ? 'Colors/Text/text-tertiary' : 'Colors/Text/text-disabled'}
                />
                <Typography variant="text-sm-semibold" sx={addButtonTextSx(canAddInvitee)}>
                  {STRINGS.addAnother}
                </Typography>
              </Box>
            )}
          </Box>

          {submitError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {submitError}
            </Typography>
          )}

          <Button
            id="onboarding-send-invites-btn"
            hierarchy="Primary"
            size="md"
            onClick={handleSubmit}
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : STRINGS.sendInvites}
          </Button>
        </Box>

        <Box sx={skipContainerSx}>
          <Typography variant="text-sm-regular" sx={skipQuestionTextSx}>
            {STRINGS.dontWantToAddFamilyMembers}
          </Typography>
          <Typography variant="text-sm-semibold" sx={skipLinkSx} onClick={handleSkip}>
            {STRINGS.skip}
          </Typography>
        </Box>
      </Box>
    </OnboardingStepContainer>
  );
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: { sm: '360px' },
  maxWidth: { sm: '360px' },
  width: { xs: '90vw', sm: 'unset' },
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: backrs.spacing['spacing-3xl'],
});

const inputFieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: backrs.spacing['spacing-xl'],
});

const inviteeRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const inputRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const roleSelectContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flex: '1 1 0',
  '& .MuiAutocomplete-inputRoot': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    '&:hover': {
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      borderColor: backrs.colors['Colors/Border/border-brand'],
      boxShadow: `0 0 0 1px ${backrs.colors['Colors/Border/border-brand']}`,
    },
  },
  '& .MuiAutocomplete-popupIndicatorOpen': {
    transform: 'none',
  },
});

const addButtonContainerSx =
  (isEnabled: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-sm'],
    cursor: isEnabled ? 'pointer' : 'default',
    opacity: isEnabled ? 1 : 0.5,
    '&:hover': {
      opacity: isEnabled ? 0.8 : 0.5,
    },
  });

const addButtonTextSx =
  (isEnabled: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: isEnabled
      ? backrs.colors['Colors/Text/text-tertiary']
      : backrs.colors['Colors/Text/text-disabled'],
  });

const skipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const skipQuestionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const skipLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});
